/**
 * AgentHandoffDashboard.js
 *
 * Full-featured dashboard for live agents to:
 *  - See all active handoffs (queue) with department/priority/reason
 *  - Claim and chat with users in real-time (SSE for live messages)
 *  - View full conversation history (bot + user + agent messages)
 *  - Resolve handoffs (optionally resume bot workflow)
 *  - Conversation replay: browse past sessions, search, filter by date
 *
 * Props:
 *  - workflowId: Workflow ID
 *  - authToken: JWT token (used only for SSE query param auth)
 *  - user: Current admin user object { username, ... }
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  UserCheck, Send, X, RefreshCw, MessageSquare,
  Clock, AlertCircle, CheckCircle2, Search,
  Filter, ChevronDown, ChevronUp, Play, ArrowLeft, Users
} from 'lucide-react';
import { apiCall, apiCallJson } from './Api';
import ConversationsBrowser from './ConversationsBrowser';
import MediaAttachment from './MediaAttachment';

// Strip provider/routing suffixes for DISPLAY only (raw value still used for APIs).
const prettyChannel = (ch) => (ch || 'web')
  .replace(/_via_interakt$/i, '')
  .replace(/_interakt$/i, '')
  .replace(/_channel$/i, '');

// ==================== Priority Badge ====================
const PriorityBadge = ({ priority }) => {
  const colors = {
    urgent: 'bg-red-100 text-red-700 border-red-300',
    high: 'bg-orange-100 text-orange-700 border-orange-300',
    normal: 'bg-blue-100 text-blue-700 border-blue-300',
    low: 'bg-gray-100 text-gray-600 border-gray-300',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${colors[priority] || colors.normal}`}>
      {priority}
    </span>
  );
};

// ==================== Agent Chat Panel ====================
const REFRESH_KEY = 'ccan_agent_refresh_ms';
const REFRESH_OPTIONS = [
  { label: '5s', ms: 5000 }, { label: '10s', ms: 10000 },
  { label: '30s', ms: 30000 }, { label: 'Off', ms: 0 },
];
const getRefreshMs = () => {
  const v = Number(localStorage.getItem(REFRESH_KEY));
  return Number.isFinite(v) && v >= 0 ? v : 5000; // default 5s
};

const AgentChatPanel = ({
  handoff, workflowId, authToken, agentName, onResolve, onBack
}) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshMs, setRefreshMs] = useState(getRefreshMs());
  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  // Stable primitives — the SSE connection keys on these ONLY, so it is never
  // rebuilt by a refresh tick or a parent re-render (which would tear down the
  // in-flight chunked stream and log ERR_INCOMPLETE_CHUNKED_ENCODING).
  const userId = handoff.user_id;
  const channel = handoff.channel || 'web';

  // Load conversation history (append-merge so periodic refresh adds new messages
  // without dropping just-sent ones or duplicating what's shown).
  const keyOf = (m) => `${m.type}|${(m.content || '').trim()}|${(m.media && m.media.download_token) || ''}`;
  const loadHistory = useCallback(async () => {
    try {
      const data = await apiCallJson(
        `/api/chatbot/workflows/${workflowId}/sessions/${channel}/${userId}/history?limit=200`
      );
      const mapped = (data.history || []).map(m => ({
        ...m,
        type: (m.metadata && m.metadata.agent_id) || m.message_type === 'agent_message'
          ? 'agent'
          : (m.type || (m.is_user ? 'user' : 'bot')),
        agent_name: m.metadata && m.metadata.agent_id,
        content: m.content,
        media: m.metadata && m.metadata.media,
        timestamp: new Date(m.timestamp)
      })).reverse();
      setMessages(prev => {
        if (!prev.length) return mapped;
        if (!mapped.length) return prev;
        const seen = new Set(prev.map(keyOf));
        const additions = mapped.filter(m => !seen.has(keyOf(m)));
        return additions.length ? [...prev, ...additions] : prev;
      });
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }, [workflowId, userId, channel]);

  // History load + auto-refresh (configurable; 0 = off) — SEPARATE from the SSE
  // effect so a refresh tick never touches the EventSource.
  useEffect(() => {
    loadHistory();
    if (!refreshMs) return;
    const iv = setInterval(loadHistory, refreshMs);
    return () => clearInterval(iv);
  }, [loadHistory, refreshMs]);

  // SSE for live messages. Keyed on stable primitives ONLY (not loadHistory /
  // refreshMs / the handoff object), so the stream stays open for the life of the
  // conversation and is not recreated every refresh. On error: close and reconnect
  // on a fixed backoff instead of a browser reconnect storm.
  useEffect(() => {
    let es = null;
    let reconnectTimer = null;
    let closed = false;

    const appendMessages = (arr) => {
      if (!arr || !arr.length) return;
      setMessages(prev => {
        const additions = arr.map(msg => ({
          type: msg.is_user ? 'user' : (msg.is_agent || msg.role === 'agent' ? 'agent' : (msg.role === 'user' ? 'user' : 'bot')),
          content: msg.content,
          media: msg.media,
          agent_name: msg.agent_id,
          timestamp: new Date(msg.timestamp || Date.now()),
        })).filter(a => {
          if (!a.content && !a.media) return false;
          return !prev.slice(-6).some(p => p.type === a.type && p.content === a.content
            && ((p.media && p.media.download_token) === (a.media && a.media.download_token)));
        });
        return additions.length ? [...prev, ...additions] : prev;
      });
    };

    const connect = () => {
      if (closed) return;
      const sseUrl = `${window.location.origin}/api/chatbot/workflows/${workflowId}/handoff/stream?user_id=${encodeURIComponent(userId)}&channel=${encodeURIComponent(channel)}${authToken ? `&token=${encodeURIComponent(authToken)}` : ''}`;
      es = new EventSource(sseUrl);
      eventSourceRef.current = es;
      es.addEventListener('message', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.messages) appendMessages(data.messages);
          else if (data.role || data.content) appendMessages([data]);
        } catch (err) { /* ignore parse errors */ }
      });
      es.addEventListener('error', () => {
        try { if (es) es.close(); } catch {}
        es = null;
        if (!closed && !reconnectTimer) {
          reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, 8000);
        }
      });
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { if (es) es.close(); } catch {}
      eventSourceRef.current = null;
    };
  }, [workflowId, userId, channel, authToken]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message as agent
  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || sending) return;

    const text = inputText.trim();
    setInputText('');
    setSending(true);

    // Optimistic update
    setMessages(prev => [...prev, {
      type: 'agent', content: text, agent_name: agentName, timestamp: new Date()
    }]);

    try {
      const resp = await apiCall(
        `/api/chatbot/workflows/${workflowId}/handoff/agent-message`,
        { method: 'POST', body: JSON.stringify({
            user_id: handoff.user_id, channel: handoff.channel || 'web', text, agent_id: agentName
        })}
      );
      if (!resp.ok) {
        console.error('Agent message failed');
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (err) {
      console.error('Agent send error:', err);
      setMessages(prev => prev.slice(0, -1));
    }
    setSending(false);
  };

  // Resolve handoff
  const handleResolve = async (resume) => {
    try {
      const resp = await apiCall(
        `/api/chatbot/workflows/${workflowId}/handoff/resolve`,
        { method: 'POST', body: JSON.stringify({
            user_id: handoff.user_id, channel: handoff.channel || 'web', resume_workflow: resume
        })}
      );
      if (resp.ok) onResolve(handoff.user_id);
    } catch (err) {
      console.error('Resolve error:', err);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-3">
          <button onClick={onBack} className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="font-semibold text-sm text-gray-800 flex items-center space-x-2">
              <UserCheck className="h-4 w-4 text-amber-600" />
              <span>{handoff.user_id}</span>
            </div>
            <div className="text-xs text-gray-500 flex items-center space-x-2">
              <span>{prettyChannel(handoff.channel)}</span>
              <PriorityBadge priority={handoff.priority || 'normal'} />
              {handoff.department && <span className="text-amber-600">• {handoff.department}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={refreshMs}
            onChange={(e) => { const v = Number(e.target.value); setRefreshMs(v); localStorage.setItem(REFRESH_KEY, String(v)); }}
            title="Auto-refresh interval"
            className="text-xs border border-gray-300 rounded px-2 py-1.5 text-gray-600 bg-white"
          >
            {REFRESH_OPTIONS.map(o => (
              <option key={o.ms} value={o.ms}>{o.ms ? `Refresh ${o.label}` : 'Refresh off'}</option>
            ))}
          </select>
          <button onClick={() => handleResolve(true)}
            className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 flex items-center space-x-1">
            <CheckCircle2 className="h-3 w-3" />
            <span>Resolve & Resume Bot</span>
          </button>
          <button onClick={() => handleResolve(false)}
            className="text-xs bg-gray-500 text-white px-3 py-1.5 rounded hover:bg-gray-600">
            Resolve Only
          </button>
        </div>
      </div>

      {handoff.reason && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
          <strong>Reason:</strong> {handoff.reason}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] p-3 rounded-lg shadow-sm text-sm ${
              msg.type === 'user'
                ? 'bg-blue-500 text-white rounded-br-none'
                : msg.type === 'agent'
                ? 'bg-amber-100 text-gray-800 border border-amber-300 rounded-bl-none'
                : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
            }`}>
              {msg.type === 'agent' && (
                <div className="text-xs text-amber-600 font-medium mb-1">🛡️ {msg.agent_name || 'Agent'}</div>
              )}
              {msg.type === 'bot' && (
                <div className="text-xs text-gray-400 mb-1">Bot</div>
              )}
              {msg.content && <p className="whitespace-pre-wrap">{msg.content}</p>}
              {msg.media && (
                <MediaAttachment
                  media={msg.media}
                  workflowId={workflowId}
                  align={msg.type === 'user' ? 'end' : 'start'}
                />
              )}
              <div className="text-xs mt-1 opacity-60">
                {msg.timestamp instanceof Date ? msg.timestamp.toLocaleTimeString() : ''}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 bg-white border-t border-gray-200 flex-shrink-0">
        <form onSubmit={handleSend} className="flex items-center space-x-2">
          <input type="text" value={inputText} onChange={(e) => setInputText(e.target.value)}
            placeholder="Type your message as agent..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          <button type="submit" disabled={!inputText.trim() || sending}
            className="p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== Conversation Replay Panel ====================
const ConversationReplay = ({ workflowId }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [channelFilter, setChannelFilter] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/chatbot/workflows/${workflowId}/sessions?limit=100`;
      if (channelFilter) url += `&channel=${channelFilter}`;
      const data = await apiCallJson(url);
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
    setLoading(false);
  }, [workflowId, channelFilter]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const loadSessionHistory = async (session) => {
    setSelectedSession(session);
    setLoading(true);
    try {
      const data = await apiCallJson(
        `/api/chatbot/workflows/${workflowId}/sessions/${session.channel}/${session.user_id}/history?limit=500`
      );
      setHistory((data.history || []).map(m => ({ ...m, timestamp: new Date(m.timestamp) })));
    } catch (err) {
      console.error('Failed to load history:', err);
    }
    setLoading(false);
  };

  const filteredSessions = sessions.filter(s => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return s.user_id.toLowerCase().includes(term) ||
           s.session_id.toLowerCase().includes(term) ||
           s.channel.toLowerCase().includes(term);
  });

  if (selectedSession) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 py-3 bg-purple-50 border-b border-purple-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <button onClick={() => { setSelectedSession(null); setHistory([]); }}
              className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <div className="font-semibold text-sm">{selectedSession.user_id}</div>
              <div className="text-xs text-gray-500">
                {prettyChannel(selectedSession.channel)} • {selectedSession.message_count} messages •
                {selectedSession.first_message && ` ${new Date(selectedSession.first_message).toLocaleDateString()}`}
              </div>
            </div>
          </div>
          <button onClick={() => loadSessionHistory(selectedSession)}
            className="text-purple-600 hover:text-purple-700">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No messages found</div>
          ) : (
            history.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] p-3 rounded-lg text-sm shadow-sm ${
                  msg.type === 'user'
                    ? 'bg-blue-500 text-white rounded-br-none'
                    : msg.type === 'agent'
                    ? 'bg-amber-100 text-gray-800 border border-amber-300 rounded-bl-none'
                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                }`}>
                  {msg.type === 'agent' && <div className="text-xs text-amber-600 font-medium mb-1">🛡️ Agent</div>}
                  {msg.type === 'bot' && <div className="text-xs text-gray-400 mb-1">Bot</div>}
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  {msg.metadata?.button_value && (
                    <div className="mt-1 text-xs opacity-70">Button: {msg.metadata.button_value}</div>
                  )}
                  {msg.metadata?.file_info && (
                    <div className="mt-1 text-xs opacity-70">📎 {msg.metadata.file_info.filename}</div>
                  )}
                  {msg.metadata?.media && (
                    <MediaAttachment
                      media={msg.metadata.media}
                      workflowId={workflowId}
                      align={msg.type === 'user' ? 'end' : 'start'}
                    />
                  )}
                  <div className="text-xs mt-1 opacity-50">
                    {msg.timestamp instanceof Date ? msg.timestamp.toLocaleString() : ''}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 bg-white border-b border-gray-200 space-y-2 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search sessions by user ID..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
          </div>
          <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Channels</option>
            <option value="web">Web</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="telegram">Telegram</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
          </select>
          <button onClick={loadSessions} className="text-purple-600 hover:text-purple-700 p-2">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">No sessions found</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredSessions.map((session, idx) => (
              <button key={idx} onClick={() => loadSessionHistory(session)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-medium text-gray-800">{session.user_id}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {prettyChannel(session.channel)} • {session.message_count} messages
                      ({session.user_messages} user, {session.bot_messages} bot)
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {session.last_message && new Date(session.last_message).toLocaleDateString()}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Main Dashboard ====================
const AgentHandoffDashboard = ({ workflowId, authToken, user }) => {
  const [activeTab, setActiveTab] = useState('queue');
  const [handoffs, setHandoffs] = useState([]);
  const [selectedHandoff, setSelectedHandoff] = useState(null);
  const [loading, setLoading] = useState(false);
  const agentName = user?.username || 'Agent';

  const loadHandoffs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCallJson(`/api/chatbot/workflows/${workflowId}/handoff/active`);
      setHandoffs(data.handoffs || []);
    } catch (err) {
      console.error('Failed to load handoffs:', err);
    }
    setLoading(false);
  }, [workflowId]);

  useEffect(() => {
    loadHandoffs();
    const interval = setInterval(loadHandoffs, 10000);
    return () => clearInterval(interval);
  }, [loadHandoffs]);

  const handleResolve = (userId) => {
    setHandoffs(prev => prev.filter(h => h.user_id !== userId));
    setSelectedHandoff(null);
  };

  if (selectedHandoff) {
    return (
      <div className="h-full flex flex-col bg-white rounded-lg shadow-lg overflow-hidden">
        <AgentChatPanel
          handoff={selectedHandoff} workflowId={workflowId} authToken={authToken}
          agentName={agentName} onResolve={handleResolve} onBack={() => setSelectedHandoff(null)}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Tab Header */}
      <div className="flex border-b border-gray-200 flex-shrink-0">
        <button onClick={() => setActiveTab('queue')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
            activeTab === 'queue' ? 'text-amber-700 border-b-2 border-amber-600 bg-amber-50' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <UserCheck className="h-4 w-4" />
          <span>Live Queue</span>
          {handoffs.length > 0 && (
            <span className="bg-amber-600 text-white text-xs rounded-full px-2 py-0.5">{handoffs.length}</span>
          )}
        </button>
        <button onClick={() => setActiveTab('replay')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
            activeTab === 'replay' ? 'text-purple-700 border-b-2 border-purple-600 bg-purple-50' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <Play className="h-4 w-4" />
          <span>Conversation Replay</span>
        </button>
        <button onClick={() => setActiveTab('all')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
            activeTab === 'all' ? 'text-purple-700 border-b-2 border-purple-600 bg-purple-50' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <Users className="h-4 w-4" />
          <span>All Conversations</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'queue' ? (
          <div className="h-full flex flex-col">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="text-sm text-gray-600">
                {handoffs.length} active handoff{handoffs.length !== 1 ? 's' : ''}
              </div>
              <button onClick={loadHandoffs} className="text-amber-600 hover:text-amber-700 flex items-center space-x-1 text-sm">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading && handoffs.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600" />
                  <p className="text-sm text-gray-500 mt-2">Loading handoffs...</p>
                </div>
              ) : handoffs.length === 0 ? (
                <div className="text-center py-12">
                  <UserCheck className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">No active handoffs</p>
                  <p className="text-gray-400 text-xs mt-1">Handoff requests will appear here in real-time</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {handoffs.map((handoff, idx) => (
                    <button key={idx} onClick={() => setSelectedHandoff(handoff)}
                      className="w-full text-left px-4 py-4 hover:bg-amber-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-sm text-gray-800">{handoff.user_id}</span>
                            <PriorityBadge priority={handoff.priority || 'normal'} />
                          </div>
                          <div className="text-xs text-gray-500 mt-1 flex items-center space-x-2">
                            <span>{handoff.channel || 'web'}</span>
                            {handoff.department && <span className="text-amber-600">📁 {handoff.department}</span>}
                          </div>
                          {handoff.reason && (
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">{handoff.reason}</div>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 flex items-center space-x-1 flex-shrink-0">
                          <Clock className="h-3 w-3" />
                          <span>{handoff.timestamp ? new Date(handoff.timestamp).toLocaleTimeString() : ''}</span>
                        </div>
                      </div>
                      {handoff.message_history?.length > 0 && (
                        <div className="mt-2 bg-gray-50 rounded p-2 text-xs text-gray-600 max-h-16 overflow-hidden">
                          {handoff.message_history.slice(-2).map((msg, mIdx) => (
                            <div key={mIdx} className="truncate">
                              <span className="font-medium">{msg.role === 'user' ? 'User' : 'Bot'}:</span> {msg.content}
                            </div>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'replay' ? (
          <ConversationReplay workflowId={workflowId} />
        ) : (
          <ConversationsBrowser
            workflowId={workflowId} agentName={agentName}
            onOpen={setSelectedHandoff}
          />
        )}
      </div>
    </div>
  );
};

export default AgentHandoffDashboard;
